import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Video, Phone, MapPin, MessageCircle, Mail, MessageSquare, Building, RefreshCw, Sparkles, ExternalLink, Plus, X, AlertTriangle } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MeetingEditor } from "@/pages/app/AllMeetings";

// Iter 253l · Client Interactions Timeline
//
// Renders a CRM-style vertical timeline of every logged interaction:
//   • Meetings (video / audio / chat / email / in-person / site / WA)
//   • Sales-CC send events (virtual "email_thread" rows)
//
// Admins can materialise historical send events into persistent meeting
// rows via the "Auto-log past sends" button — powered by
// POST /workspaces/{ws}/interactions/backfill-from-sends.

const MODES = {
  video_call: { label: "Video call", icon: Video, color: "#2563eb" },
  audio_call: { label: "Audio call", icon: Phone, color: "#0891b2" },
  in_person: { label: "In-person", icon: MapPin, color: "#059669" },
  chat_dm: { label: "Chat / DM", icon: MessageCircle, color: "#7c3aed" },
  email_thread: { label: "Email thread", icon: Mail, color: "#dc2626" },
  whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "#16a34a" },
  site_visit: { label: "Site visit", icon: Building, color: "#ea580c" },
  // R155 · Feb 2026 — Escalation touchpoint (call / email that formally
  // escalates an issue). Rose tone so it stands out on the timeline.
  escalation: { label: "Escalation", icon: AlertTriangle, color: "#e11d48" },
};

export default function ClientInteractionsTab({ clientId, wsId }) {
  const [rows, setRows] = useState([]);
  const [modeFilter, setModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [attendeeFilter, setAttendeeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  // Iter 253m · Log-from-here + drawer.
  const [creating, setCreating] = useState(null);   // {} → open editor
  const [clientProjects, setClientProjects] = useState([]);
  const [drawerRow, setDrawerRow] = useState(null);

  useEffect(() => {
    // Fetch projects tied to this client — needed so MeetingEditor's project
    // picker only shows this client's projects when logging from here.
    let cancel = false;
    api.get(`/workspaces/${wsId}/projects?client_id=${clientId}`)
      .then((r) => { if (!cancel) setClientProjects(r.data || []); })
      .catch(() => { if (!cancel) setClientProjects([]); });
    return () => { cancel = true; };
  }, [wsId, clientId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Iter 253n · Compose filters. Backend accepts ISO strings for
      // `date_from` / `date_to` and a free-text attendee match (email
      // fragment, name fragment, or exact user_id).
      const params = new URLSearchParams();
      if (modeFilter) params.set("mode", modeFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (attendeeFilter.trim()) params.set("attendee", attendeeFilter.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/clients/${clientId}/interactions${qs}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load interactions")); }
    finally { setLoading(false); }
  }, [clientId, modeFilter, dateFrom, dateTo, attendeeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtersActive = !!(modeFilter || dateFrom || dateTo || attendeeFilter.trim());
  const clearFilters = () => { setModeFilter(""); setDateFrom(""); setDateTo(""); setAttendeeFilter(""); };

  const doBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/interactions/backfill-from-sends`,
        { client_id: clientId, limit: 500 });
      if (data.created > 0) {
        toast.success(`Materialised ${data.created} past send(s) as interactions.`);
      } else if (data.skipped_already_logged > 0) {
        toast.info("All past sends already logged. Nothing to backfill.");
      } else if (data.skipped_no_project > 0) {
        toast.warning(`Skipped ${data.skipped_no_project} send(s) — no project to attach.`);
      } else {
        toast.info("No sends to backfill for this client yet.");
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
    finally { setBackfilling(false); }
  };

  const grouped = useMemo(() => {
    const groups = {};
    rows.forEach((r) => {
      const day = (r.at || "").slice(0, 10) || "—";
      groups[day] = groups[day] || [];
      groups[day].push(r);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="client-interactions-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={modeFilter || "_all"} onValueChange={(v) => setModeFilter(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-40 text-xs rounded-none" data-testid="interactions-mode-filter"><SelectValue placeholder="All modes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All modes</SelectItem>
            {Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex items-center gap-1" data-testid="interactions-date-range">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="h-8 w-36 text-xs rounded-none" placeholder="From"
                 data-testid="interactions-date-from" aria-label="From date" />
          <span className="text-[10px] text-muted-foreground">→</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="h-8 w-36 text-xs rounded-none" placeholder="To"
                 data-testid="interactions-date-to" aria-label="To date" />
        </div>
        <Input type="text" value={attendeeFilter} onChange={(e) => setAttendeeFilter(e.target.value)}
               className="h-8 w-44 text-xs rounded-none" placeholder="Attendee (name/email)"
               data-testid="interactions-attendee-filter" />
        {filtersActive && (
          <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs px-2" onClick={clearFilters} data-testid="interactions-clear-filters">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={load} disabled={loading} data-testid="interactions-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" className="rounded-none h-8 text-xs ml-auto" onClick={() => setCreating({})} data-testid="interactions-log-btn">
          <Plus className="w-3 h-3 mr-1" /> Log interaction
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={doBackfill} disabled={backfilling} data-testid="interactions-backfill">
          <Sparkles className="w-3 h-3 mr-1" /> {backfilling ? "Backfilling…" : "Auto-log past sends"}
        </Button>
        <span className="text-[10px] text-muted-foreground w-full sm:w-auto sm:ml-2">
          {rows.length} interaction{rows.length === 1 ? "" : "s"}{filtersActive ? " (filtered)" : ""}
        </span>
      </div>

      {loading && (
        <div className="p-8 text-center text-sm text-muted-foreground" data-testid="interactions-loading">Loading…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-none" data-testid="interactions-empty">
          No interactions logged yet. Start a meeting or send a quote/invoice — they'll appear here.
        </div>
      )}
      {!loading && grouped.map(([day, group]) => (
        <div key={day} className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid={`interactions-day-${day}`}>
            {day === "—" ? "Undated" : new Date(day).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </div>
          <div className="border-l-2 border-border pl-4 space-y-2">
            {group.map((r) => {
              const cfg = MODES[r.mode] || MODES.video_call;
              const Icon = cfg.icon;
              return (
                <div key={`${r.kind}-${r.id}`} className="border border-border bg-card p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                     onClick={() => setDrawerRow(r)}
                     data-testid={`interactions-row-${r.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0"
                         style={{ background: `${cfg.color}20`, color: cfg.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate" data-testid={`interactions-title-${r.id}`}>
                          {r.title || "Untitled"}
                        </span>
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: cfg.color, color: cfg.color }}
                                data-testid={`interactions-mode-${r.id}`}>
                          {cfg.label}
                        </Badge>
                        {r.is_logged && (
                          <Badge variant="secondary" className="text-[9px]" data-testid={`interactions-logged-${r.id}`}>Logged</Badge>
                        )}
                        {r.kind === "send_event" && (
                          <Badge variant="outline" className="text-[9px]" data-testid={`interactions-source-${r.id}`}>
                            {r.doc_kind || "send"}{r.doc_no ? ` · ${r.doc_no}` : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span>{r.at ? new Date(r.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                        {r.duration_minutes > 0 && (<><span>·</span><span>{r.duration_minutes}m</span></>)}
                        {r.email_subject && (<><span>·</span><span className="truncate">Subject: {r.email_subject}</span></>)}
                        {r.phone_number && (<><span>·</span><span>{r.phone_number}</span></>)}
                        {r.chat_platform && (<><span>·</span><span>{r.chat_platform}</span></>)}
                        {r.location && (<><span>·</span><span className="truncate">{r.location}</span></>)}
                        {(r.external_attendees || []).length > 0 && (
                          <>
                            <span>·</span>
                            <span className="truncate">
                              {(r.external_attendees || []).slice(0, 3).map((e) => e.name || e.email).join(", ")}
                              {(r.external_attendees || []).length > 3 ? "…" : ""}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {r.video_url && (
                        <a href={r.video_url} target="_blank" rel="noreferrer"
                            className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`interactions-video-${r.id}`}>
                          <ExternalLink className="w-3 h-3" /> Join
                        </a>
                      )}
                      {r.chat_link && (
                        <a href={r.chat_link} target="_blank" rel="noreferrer"
                            className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`interactions-chat-link-${r.id}`}>
                          <ExternalLink className="w-3 h-3" /> Open thread
                        </a>
                      )}
                      {r.recording_url && (
                        <a href={r.recording_url} target="_blank" rel="noreferrer"
                            className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`interactions-recording-${r.id}`}>
                          <ExternalLink className="w-3 h-3" /> Recording
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {creating && (
        <MeetingEditor
          meeting={{ __logged: true }}
          projects={clientProjects}
          onClose={() => setCreating(null)}
          onSaved={() => { setCreating(null); load(); }}
        />
      )}
      <Sheet open={!!drawerRow} onOpenChange={(v) => { if (!v) setDrawerRow(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="interaction-drawer">
          {drawerRow && (
            <>
              <SheetHeader>
                <SheetTitle className="text-lg" data-testid="interaction-drawer-title">
                  {drawerRow.title || "Interaction"}
                </SheetTitle>
                <SheetDescription>
                  {drawerRow.at ? new Date(drawerRow.at).toLocaleString() : ""}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{(MODES[drawerRow.mode] || MODES.video_call).label}</Badge>
                  {drawerRow.is_logged && <Badge variant="secondary">Logged (past)</Badge>}
                  {drawerRow.kind === "send_event" && (
                    <Badge variant="outline">{drawerRow.doc_kind}{drawerRow.doc_no ? ` · ${drawerRow.doc_no}` : ""}</Badge>
                  )}
                </div>
                {drawerRow.email_subject && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Subject</div>
                    <div className="text-sm">{drawerRow.email_subject}</div>
                  </div>
                )}
                {(drawerRow.external_attendees || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">External attendees</div>
                    <div className="flex flex-wrap gap-1" data-testid="interaction-drawer-attendees">
                      {(drawerRow.external_attendees || []).map((e, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{e.name || e.email}{e.email && e.name ? ` · ${e.email}` : ""}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {drawerRow.location && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Location</div>
                    <div className="text-sm">{drawerRow.location}</div>
                  </div>
                )}
                {drawerRow.phone_number && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Phone</div>
                    <div className="text-sm">{drawerRow.phone_number}</div>
                  </div>
                )}
                {drawerRow.chat_platform && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Platform</div>
                    <div className="text-sm">{drawerRow.chat_platform}</div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                  {drawerRow.video_url && <a href={drawerRow.video_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Join</a>}
                  {drawerRow.chat_link && <a href={drawerRow.chat_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open thread</a>}
                  {drawerRow.recording_url && <a href={drawerRow.recording_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Recording</a>}
                  {drawerRow.kind === "meeting" && (
                    <a href={`/app/meetings/${drawerRow.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1 ml-auto" data-testid="interaction-drawer-open-full">
                      <ExternalLink className="w-3 h-3" /> Open full page
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
